<template>
  <div class="block preview">
    <div class="block-header">
      <h4 class="mr-auto">
        Preview
      </h4>

      <a-button
        v-if="!showCode"
        class="mr-2 is-small"
        hotkeys="ctrl+p"
        :loading="loadingTemplatePreview"
        @click="getPreview(true)">
        Preview Template
      </a-button>

      <a-button
        v-if="showCode && type === 'module'"
        class="mr-2 is-small"
        :loading="loadingDebug"
        @click="getDebug(false)">
        Debug
      </a-button>

      <div
        v-if="!showCode"
        class="preview-button-group">
        <a-button
          class="is-small preview-button"
          hotkeys="ctrl+ shift + p"
          :loading="loadingDataPreview"
          @click="getPreview(false)">
          Preview with Data
        </a-button>
        <b-datepicker
          v-model="previewDate"
          position="is-bottom-left"
          :mobile-native="false">
          <template #trigger>
            <b-button
              icon-right="chevron-down"
              class="is-white is-small">
              {{ previewDateFormatted }}
            </b-button>
          </template>
        </b-datepicker>
      </div>

      <a-button
        v-if="canPreviewEmailBody && !showCode"
        class="mr-2 is-small"
        :loading="loadingEmailPreview"
        title="Renders this through the email body renderer, in the email's own chrome."
        @click="getEmailBodyPreview">
        Preview as Email
      </a-button>

      <b-checkbox
        v-if="hasSummarizer && !showCode"
        v-model="includeSummaries"
        class="is-small mr-2 summarize-toggle"
        title="Each preview with this on spends a model call. Leave it off while you work on the rest of the report.">
        Generate summaries
      </b-checkbox>

      <button
        class="button output-switcher is-small"
        @click="switchOutputMode">
        {{ showCode ? "View PDF" : "View Code" }}
      </button>
    </div>
    <div class="block-body">
      <div
        class="output"
        :class="{ 'is-email': !showCode && emailBodyHTML }">
        <p
          v-if="type === 'summarizer' && !showCode"
          class="preview-note">
          Previews the whole report, using the last published version of this
          module. Publish your changes to see them here.
        </p>
        <iframe
          v-if="!showCode && emailBodyHTML"
          class="email-body-output"
          title="Email body preview"
          sandbox=""
          :srcdoc="emailBodyHTML" />

        <div
          v-else-if="!showCode"
          class="preview-output">
          <a
            v-if="!showCode && reportPreviewURL"
            class="button pdf-download-btn is-small"
            :href="reportPreviewURL"
            target="_blank">Download</a>

          <pdf
            v-if="reportPreviewURL"
            :src="reportPreviewURL" />
        </div>

        <div
          v-else
          class="output p-0">
          <v-jsoneditor
            v-model="module"
            :options="{ mode: 'code' }"
            :plus="false"
            height="100%"
            @error="onError" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import VJsoneditor from 'v-jsoneditor'
  import pdf from 'vue-pdf'

  import { formatDate } from '@/utils/Date'
  import moment from 'moment'

  export default {
    name: 'PreviewAndJsonEditor',
    components: {
      pdf,
      VJsoneditor,
    },
    props: {
      value: {
        type: [Object, Array,],
        default: () => {},
      },
      type: {
        type: String,
        required: true,
      },
      fullReport: {
        type: Object,
        default: () => {},
      },
      defaultTimeRange: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        showCode: false,
        // Off by default and not remembered: a summary costs a model call
        // per preview. Off, the backend skips the summary pass entirely.
        includeSummaries: false,

        modulePreviewURL: null,
        reportPreviewURL: null,
        // The last email body rendered. Set, it displaces the PDF viewer —
        // running any other preview clears it, so the pane always shows
        // whichever preview was asked for last.
        emailBodyHTML: null,
        loadingEmailPreview: false,
        loadingTemplatePreview: false,
        loadingDataPreview: false,
        loadingDebug: false,
        previewDate: moment().subtract(1, 'days').toDate(),
      }
    },

    computed: {
      previewDateFormatted () {
        return formatDate(this.previewDate, 'll')
      },

      // The modules this preview will actually render, mirroring how the
      // payloads below are built.
      previewModules () {
        if (this.type === 'summarizer') return this.fullReport?.modules || []
        if (this.type === 'report') return Array.isArray(this.module) ? this.module : []
        return this.module ? [this.module,] : []
      },

      // Not gated on AI_REPORT_SUMMARIES: the flag governs adding a
      // summarizer, and someone without it previewing a report that already
      // has one still needs to decide whether to pay for the call.
      hasSummarizer () {
        return this.previewModules.some(m => m?.type === 'SummarizerModule')
      },

      // Gated with the feature that puts a report in an email body at all:
      // without it nothing a company sends uses this renderer, so previewing
      // one would be previewing a layout they never receive.
      canPreviewEmailBody () {
        return this.$hasFeatureFlag('EMBED_REPORTS_IN_EMAIL') &&
          ['report', 'summarizer', 'module',].includes(this.type)
      },

      module: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
    },

    errorCaptured (err) {
      if (err?.message?.includes('catch')) return false
      return true
    },
    async mounted () {
      window.addEventListener('keydown', this.shortcutHandler)
    },
    beforeDestroy () {
      window.removeEventListener('keydown', this.shortcutHandler)
    },
    methods: {
      switchOutputMode () {
        this.showCode = !this.showCode
      },
      async getDebug (fakeData = true, errorMessage = 'Error Loading Debug, Fix the errors and try again') {
        this.loadingDebug = true
        try {
          let response = null
          // A summarizer has no data of its own — what it renders depends
          // on the modules around it — so its preview is the whole report.
          // The report preview loads each module's config from the database,
          // so this shows the last published version of this module; the
          // banner above the pane says so.
          if (this.type === 'report' || this.type === 'summarizer') {
          // Not Supported
          } else if (this.type === 'module') {
            response = await this.$apis.pdfReports.getPdfReportModuleDebug({
              report_module: this.module,
              run_date: formatDate(this.previewDate, 'YYYY-MM-DD'),
              use_fake_data: fakeData,
              default_time_range: this.defaultTimeRange,
            })

            if (response?.errors) {
              this.$buefy.toast.open({
                message: errorMessage,
                type: 'is-warning',
              })
              this.$emit('validation-errors', this.stripErrorPrefix(response.errors))
            } else if (!response) {
              this.$buefy.toast.open({
                message: 'Backend Error, Report this to the devs',
                type: 'is-warning',
              })
            } else {
              this.$emit('validation-errors', [])
            }
          }
        } catch (e) {
          console.log('error loading full debug')
          console.log(e)
        }

        this.loadingDebug = false
      },
      async getPreview (fakeData = true) {
        this.emailBodyHTML = null
        if (fakeData) {
          this.loadingTemplatePreview = true
        } else {
          this.loadingDataPreview = true
        }

        try {
          // A summarizer has no data of its own, so previewing one means
          // previewing the report around it.
          if (this.type === 'report' || this.type === 'summarizer') {
            const response = await this.$apis.pdfReports.getPdfReportFullPreview({
              report: {
                ...this.fullReport,
                modules: this.type === 'summarizer'
                  ? (this.fullReport?.modules || [])
                  : this.module,
                style: { is_a11y: false, },
              },
              run_date: formatDate(this.previewDate, 'YYYY-MM-DD'),
              use_fake_data: fakeData,
              include_summaries: this.includeSummaries,
            })
            if (!response) {
              this.$buefy.toast.open({
                message: 'Backend Error, Report this to the devs',
                type: 'is-warning',
              })
            } else {
              this.handlePreviewMetadata(response)
              this.reportPreviewURL = this.getPDFfromBase64(response.pdf)
            }
          } else if (this.type === 'module') {
            const response = await this.$apis.pdfReports.getPdfReportModulePreview({
              report_module: this.module,
              run_date: formatDate(this.previewDate, 'YYYY-MM-DD'),
              use_fake_data: fakeData,
              default_time_range: this.defaultTimeRange,
              include_summaries: this.includeSummaries,
            })
            if (!response) {
              this.$buefy.toast.open({
                message: 'Error rendering module, report to ingest support.',
                type: 'is-danger',
              })
            } else if (response.errors) {
              this.$buefy.toast.open({
                message: 'Error loading preview, fix the errors and try again',
                type: 'is-warning',
              })
              this.$emit('validation-errors', this.stripErrorPrefix(response.errors))
            } else {
              this.$emit('validation-errors', [])
              if (response.truncated) {
                this.$buefy.toast.open({
                  message: 'Some rows were truncated due to the preview row limit',
                  type: 'is-warning',
                  duration: 5000,
                })
              }
              this.reportPreviewURL = this.getPDFfromBase64(response.pdf)
            }
          }
        } catch (e) {
          if (this.type === 'module') {
            this.$buefy.toast.open({
              message: 'Error rendering module, report to ingest support.',
              type: 'is-danger',
            })
          }
          console.log('error loading full preview')
        }

        if (fakeData) {
          this.loadingTemplatePreview = false
        } else {
          this.loadingDataPreview = false
        }
      },

      // Real data, like "Preview with Data": the point of looking at a
      // report in the email chrome is seeing the figures in it.
      async getEmailBodyPreview () {
        this.loadingEmailPreview = true
        try {
          const common = {
            run_date: formatDate(this.previewDate, 'YYYY-MM-DD'),
            use_fake_data: false,
            include_summaries: this.includeSummaries,
          }
          const response = this.type === 'module'
            ? await this.$apis.pdfReports.getPdfReportModuleEmailBodyPreview({
              ...common,
              report_module: this.module,
              default_time_range: this.defaultTimeRange,
            })
            : await this.$apis.pdfReports.getPdfReportEmailBodyPreview({
              ...common,
              report: {
                ...this.fullReport,
                modules: this.type === 'summarizer'
                  ? (this.fullReport?.modules || [])
                  : this.module,
                style: { is_a11y: false, },
              },
            })

          if (!response) {
            this.$buefy.toast.open({
              message: 'Error rendering the email body, report to ingest support.',
              type: 'is-danger',
            })
          } else if (response.errors) {
            this.$buefy.toast.open({
              message: 'Error loading preview, fix the errors and try again',
              type: 'is-warning',
            })
            this.$emit('validation-errors', this.stripErrorPrefix(response.errors))
          } else {
            this.$emit('validation-errors', [])
            if (response.truncated) {
              this.$buefy.toast.open({
                message: 'Some rows were truncated due to the preview row limit',
                type: 'is-warning',
                duration: 5000,
              })
            }
            this.emailBodyHTML = response.html
          }
        } catch (e) {
          this.$buefy.toast.open({
            message: 'Error rendering the email body, report to ingest support.',
            type: 'is-danger',
          })
          console.log('error loading email body preview')
          console.log(e)
        }

        this.loadingEmailPreview = false
      },

      getPDFfromBase64 (base64) {
        if (!base64) return null
        const binary = atob(base64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i)
        }
        const blob = new Blob([bytes,], { type: 'application/pdf', })
        return URL.createObjectURL(blob)
      },

      handlePreviewMetadata (responseData) {
        if (responseData.truncated) {
          this.$buefy.toast.open({
            message: 'Some rows were truncated due to the preview row limit',
            type: 'is-warning',
            duration: 5000,
          })
        }
        const brokenModules = responseData.broken_modules
        if (brokenModules && brokenModules.length) {
          this.$emit('broken-modules', brokenModules)
          const names = brokenModules.map((m) => m.title || m.id).join(', ')
          this.$buefy.toast.open({
            message: `Some modules failed to render: ${names}`,
            type: 'is-danger',
            duration: 8000,
          })
        }
      },

      stripErrorPrefix (errors) {
        if (!Array.isArray(errors)) return []
        return errors
          .filter(e => e.loc && e.loc[0] === 'report_module')
          .map(e => ({ ...e, loc: e.loc.slice(1), }))
      },

      onError (e) {
        console.log('error', e)
      },
    },
  }
</script>
<style lang="scss">
.block {
  margin-bottom: 6px;
  border: $border-1;
  border-radius: 3px;
  background-color: white;
  height: 100%;
  flex-direction: column;
  display: flex;

  .block-header {
    min-height: 40px;
    align-items: center;
    padding: 0 5px;
    border-bottom: $border-1;
    background-color: white;
    display: flex;

    h4 {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      padding: 0 10px;
      text-transform: capitalize;
      margin: 0;
      color: black;
    }
  }
  .block-body {
    padding: 0 10px;
    background-color: #fbfbfb;
    height: 100%;
    flex-direction: column;
    display: flex;
  }

  &.library {
    border: $border-1;
    .library-items {
      display: flex;
      flex-direction: column;
    }
    .block-body {
      padding-bottom: 0;
    }
  }

  &.layout {
    max-height: 100%;
    overflow: hidden;

    .block-body {
      height: calc(100% - 40px);
      padding: 0;
    }
  }

  &.preview {
    height: 100%;

    .preview-output {
      position: relative;
      .pdf-download-btn {
        margin: 10px;
      }
    }

    .preview-button-group {
      display: flex;
      align-items: center;
      margin-right: 10px;
      border: $border-1;
      border-color: hsl(0, 0%, 86%);

      .preview-button {
        border-right: 1px solid hsl(0, 0%, 86%);
      }
    }
    .block-header {
      width: 100%;
    }

    .block-body {
      width: 100%;
      height: 100%;
      display: flex;
      overflow: hidden;
    }

    .output {
      background-color: white;
      height: 100%;
      overflow: auto;
      // min-width:0 is what lets it actually shrink — without it a wide PDF
      // forces overflow instead of yielding space.
      flex: 1 1 auto;
      min-width: 0;

      // The body fills the pane by growing as a flex item rather than by
      // percentage height: an iframe whose `height: 100%` fails to resolve
      // falls back to its intrinsic 300x150, and the PDF viewer never
      // exercised the height chain because a canvas carries its own size.
      &.is-email {
        display: flex;
        flex-direction: column;
        // The frame scrolls its own content; a scrollbar here as well would
        // be a second one for the same overflow.
        overflow: hidden;
      }
    }

    .email-body-output {
      flex: 1 1 auto;
      width: 100%;
      min-height: 0;
      border: 0;
      // White, not the pane's ground: the shell paints its own faint grey
      // and the gap around it should read as the mail client's.
      background: white;
    }
  }
}
</style>
